import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Trash2, UserPlus, ArrowLeft, Search } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import api from "@/lib/api";
import { useAuth, isAdmin, ROLE_LABELS, ROLES } from "@/lib/auth.jsx";
import { money } from "@/lib/format";
import { CatalogSection } from "@/components/CatalogSection";
import { getApiErrorMessage } from "@/lib/apiError";

function CompanySection({ settings, save }) {
  const [s, setS] = useState(settings);
  useEffect(() => setS(settings), [settings]);
  const set = (k, v) => setS((x) => ({ ...x, [k]: v }));
  return (
    <Card className="rounded-xl border-slate-200 p-5 space-y-4">
      <div className="text-sm font-semibold text-slate-900">
        Company info (used on customer-facing quotes)
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <Label>Company name</Label>
          <Input
            value={s.company_name || ""}
            onChange={(e) => set("company_name", e.target.value)}
            data-testid="settings-company-name"
          />
        </div>
        <div>
          <Label>Tagline</Label>
          <Input
            value={s.company_tagline || ""}
            onChange={(e) => set("company_tagline", e.target.value)}
          />
        </div>
        <div className="md:col-span-2">
          <Label>Address</Label>
          <Input
            value={s.company_address || ""}
            onChange={(e) => set("company_address", e.target.value)}
          />
        </div>
        <div>
          <Label>Phone</Label>
          <Input
            value={s.company_phone || ""}
            onChange={(e) => set("company_phone", e.target.value)}
          />
        </div>
        <div>
          <Label>Email</Label>
          <Input
            value={s.company_email || ""}
            onChange={(e) => set("company_email", e.target.value)}
          />
        </div>
      </div>
      <div className="flex justify-end">
        <Button
          onClick={() => save(s)}
          className="bg-[#0F2A47] hover:bg-[#14385E] text-white"
          data-testid="settings-company-save"
        >
          Save
        </Button>
      </div>
    </Card>
  );
}

function PricingSection({ settings, save }) {
  const [s, setS] = useState(settings);
  useEffect(() => setS(settings), [settings]);
  const num = (k, v) => setS((x) => ({ ...x, [k]: parseFloat(v || 0) }));
  return (
    <Card className="rounded-xl border-slate-200 p-5 space-y-4">
      <div className="text-sm font-semibold text-slate-900">
        Pricing constants
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        <div>
          <Label>Sales tax rate (decimal, e.g. 0.03)</Label>
          <Input
            type="number"
            step="0.001"
            value={s.sales_tax_rate ?? ""}
            onChange={(e) => num("sales_tax_rate", e.target.value)}
          />
        </div>
        <div>
          <Label>Material surcharge ($)</Label>
          <Input
            type="number"
            step="50"
            value={s.material_surcharge ?? ""}
            onChange={(e) => num("material_surcharge", e.target.value)}
          />
        </div>
        <div>
          <Label>State dues per floor ($)</Label>
          <Input
            type="number"
            step="10"
            value={s.state_dues_per_floor ?? ""}
            onChange={(e) => num("state_dues_per_floor", e.target.value)}
          />
        </div>
        <div>
          <Label>National / MHI dues ($)</Label>
          <Input
            type="number"
            step="5"
            value={s.national_mhi_dues ?? ""}
            onChange={(e) => num("national_mhi_dues", e.target.value)}
          />
        </div>
        <div>
          <Label>Admin fee % of margin</Label>
          <Input
            type="number"
            step="0.01"
            value={s.admin_fee_pct ?? ""}
            onChange={(e) => num("admin_fee_pct", e.target.value)}
          />
        </div>
        <div>
          <Label>Agent commission % of remainder</Label>
          <Input
            type="number"
            step="0.01"
            value={s.agent_comm_pct ?? ""}
            onChange={(e) => num("agent_comm_pct", e.target.value)}
          />
        </div>
        <div>
          <Label>Loan fee ($)</Label>
          <Input
            type="number"
            step="50"
            value={s.loan_fee ?? ""}
            onChange={(e) => num("loan_fee", e.target.value)}
          />
        </div>
        <div>
          <Label>Net take home floor ($)</Label>
          <Input
            type="number"
            step="500"
            value={s.take_home_floor ?? ""}
            onChange={(e) => num("take_home_floor", e.target.value)}
          />
        </div>
        <div>
          <Label>MSRP markup over cost</Label>
          <Input
            type="number"
            step="0.01"
            value={s.msrp_markup_over_cost ?? ""}
            onChange={(e) => num("msrp_markup_over_cost", e.target.value)}
          />
        </div>
      </div>
      <div className="flex justify-end">
        <Button
          onClick={() => save(s)}
          className="bg-[#0F2A47] hover:bg-[#14385E] text-white"
          data-testid="settings-pricing-save"
        >
          Save
        </Button>
      </div>
    </Card>
  );
}

function TextSection({ settings, save }) {
  const [s, setS] = useState(settings);
  useEffect(() => setS(settings), [settings]);
  return (
    <Card className="rounded-xl border-slate-200 p-5 space-y-4">
      <div className="text-sm font-semibold text-slate-900">
        Disclaimer & next steps
      </div>
      <div>
        <Label>Default disclaimer</Label>
        <Textarea
          rows={4}
          value={s.default_disclaimer || ""}
          onChange={(e) => setS({ ...s, default_disclaimer: e.target.value })}
          data-testid="settings-default-disclaimer"
        />
      </div>
      <div>
        <Label>Default next steps</Label>
        <Textarea
          rows={4}
          value={s.default_next_steps || ""}
          onChange={(e) => setS({ ...s, default_next_steps: e.target.value })}
        />
      </div>
      <div className="flex justify-end">
        <Button
          onClick={() => save(s)}
          className="bg-[#0F2A47] hover:bg-[#14385E] text-white"
          data-testid="settings-text-save"
        >
          Save
        </Button>
      </div>
    </Card>
  );
}

function categoryLabel(category) {
  if (category === "mandatory_services") return "Mandatory services";
  if (category === "addons") return "Add-ons";
  if (category === "site_work") return "Site work";
  return category || "Uncategorized";
}

function priceLabel(value) {
  const numeric = Number(value || 0);
  return numeric > 0 ? money(numeric) : "TBD";
}

function TemplateMeta({ template }) {
  const pricingType = template.pricing_type || template.pricingType || "";
  const vendor = template.vendor || "";
  if (!pricingType && !vendor) return null;
  return (
    <div className="flex flex-wrap gap-1.5 mt-1">
      {pricingType && (
        <span className="rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-[11px] text-slate-600">
          {pricingType}
        </span>
      )}
      {vendor && (
        <span className="rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-[11px] text-slate-600">
          {vendor}
        </span>
      )}
    </div>
  );
}

function TemplatesSection() {
  const [tpls, setTpls] = useState([]);
  const [form, setForm] = useState({
    category: "mandatory_services",
    description: "",
    default_unit_price: 0,
    default_cost: 0,
    vendor: "",
  });
  const [search, setSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");

  const load = () => api.get("/templates").then((r) => setTpls(r.data));
  useEffect(() => {
    load();
  }, []);

  const create = async () => {
    if (!form.description) return toast.error("Description required");
    try {
      await api.post("/templates", form);
      setForm({
        category: "mandatory_services",
        description: "",
        default_unit_price: 0,
        default_cost: 0,
        vendor: "",
      });
      load();
      toast.success("Template added");
    } catch (err) {
      toast.error(getApiErrorMessage(err, "Save failed"));
    }
  };
  const remove = async (template) => {
    const confirmed = window.confirm(
      `Delete template "${template.description}"?\n\nThis removes it from future quote selection.`,
    );
    if (!confirmed) return;
    await api.delete(`/templates/${template.id}`);
    load();
    toast.success("Template removed");
  };

  const filteredTemplates = useMemo(() => {
    const term = search.trim().toLowerCase();
    return tpls.filter((template) => {
      const matchesCategory =
        categoryFilter === "all" || template.category === categoryFilter;
      const haystack = [
        template.description,
        template.vendor,
        template.category,
        template.pricing_type,
        template.pricingType,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      const matchesSearch = !term || haystack.includes(term);
      return matchesCategory && matchesSearch;
    });
  }, [tpls, search, categoryFilter]);

  const grouped = filteredTemplates.reduce((acc, t) => {
    (acc[t.category] = acc[t.category] || []).push(t);
    return acc;
  }, {});

  const totalTbd = filteredTemplates.filter(
    (t) => Number(t.default_unit_price || 0) <= 0,
  ).length;

  return (
    <Card className="rounded-xl border-slate-200 p-5 space-y-5">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="text-sm font-semibold text-slate-900">
            Line item templates
          </div>
          <div className="text-xs text-slate-500 mt-1">
            {filteredTemplates.length} visible · {totalTbd} TBD/zero-price item
            {totalTbd === 1 ? "" : "s"}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
        <Select
          value={form.category}
          onValueChange={(v) => setForm({ ...form, category: v })}
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="mandatory_services">
              Mandatory services
            </SelectItem>
            <SelectItem value="addons">Add-ons</SelectItem>
            <SelectItem value="site_work">Site work</SelectItem>
          </SelectContent>
        </Select>
        <Input
          placeholder="Description"
          value={form.description}
          onChange={(e) => setForm({ ...form, description: e.target.value })}
          className="md:col-span-2"
          data-testid="settings-template-description"
        />
        <Input
          type="number"
          placeholder="Default price"
          value={form.default_unit_price}
          onChange={(e) =>
            setForm({
              ...form,
              default_unit_price: parseFloat(e.target.value || 0),
            })
          }
        />
        <Input
          type="number"
          placeholder="Default cost"
          value={form.default_cost}
          onChange={(e) =>
            setForm({ ...form, default_cost: parseFloat(e.target.value || 0) })
          }
        />
      </div>
      <Button
        onClick={create}
        className="bg-[#0F2A47] hover:bg-[#14385E] text-white"
        data-testid="settings-template-add-button"
      >
        <Plus className="mr-1 h-4 w-4" /> Add template
      </Button>

      <div className="grid grid-cols-1 md:grid-cols-[1fr_220px] gap-3 rounded-xl border border-slate-200 bg-slate-50 p-3">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input
            placeholder="Search service, vendor, category..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9 bg-white"
            data-testid="settings-template-search"
          />
        </div>
        <Select value={categoryFilter} onValueChange={setCategoryFilter}>
          <SelectTrigger
            className="bg-white"
            data-testid="settings-template-category-filter"
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All categories</SelectItem>
            <SelectItem value="mandatory_services">
              Mandatory services
            </SelectItem>
            <SelectItem value="addons">Add-ons</SelectItem>
            <SelectItem value="site_work">Site work</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {[
        ["mandatory_services", "Mandatory services"],
        ["addons", "Add-ons"],
        ["site_work", "Site work"],
      ]
        .filter(([cat]) => categoryFilter === "all" || categoryFilter === cat)
        .map(([cat, label]) => {
          const rows = grouped[cat] || [];
          return (
            <div key={cat} className="space-y-2">
              <div className="flex items-center justify-between">
                <div className="text-xs uppercase tracking-wide text-slate-500">
                  {label}
                </div>
                <div className="text-xs text-slate-400">
                  {rows.length} item{rows.length === 1 ? "" : "s"}
                </div>
              </div>

              {/* Mobile cards */}
              <div className="lg:hidden divide-y divide-slate-100 rounded-xl border border-slate-200 overflow-hidden">
                {rows.map((t) => (
                  <div key={t.id} className="p-4 bg-white space-y-3">
                    <div>
                      <div className="font-medium text-slate-900">
                        {t.description}
                      </div>
                      <TemplateMeta template={t} />
                    </div>
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      <div className="rounded-lg bg-slate-50 border border-slate-200 p-2">
                        <div className="text-[11px] uppercase text-slate-500">
                          Default price
                        </div>
                        <div className="font-semibold tabular text-slate-900">
                          {priceLabel(t.default_unit_price)}
                        </div>
                      </div>
                      <div className="rounded-lg bg-slate-50 border border-slate-200 p-2">
                        <div className="text-[11px] uppercase text-slate-500">
                          Default cost
                        </div>
                        <div className="font-semibold tabular text-slate-700">
                          {priceLabel(t.default_cost)}
                        </div>
                      </div>
                    </div>
                    <div className="flex justify-end">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => remove(t)}
                        className="border-red-200 text-red-700 hover:bg-red-50 hover:text-red-800"
                      >
                        <Trash2 className="mr-1.5 h-3.5 w-3.5" /> Delete
                      </Button>
                    </div>
                  </div>
                ))}
                {rows.length === 0 && (
                  <div className="p-6 text-center text-sm text-slate-500 bg-white">
                    No templates match
                  </div>
                )}
              </div>

              {/* Desktop table */}
              <div className="hidden lg:block">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Description</TableHead>
                      <TableHead>Category</TableHead>
                      <TableHead>Type / Vendor</TableHead>
                      <TableHead className="text-right">
                        Default price
                      </TableHead>
                      <TableHead className="text-right">Default cost</TableHead>
                      <TableHead />
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {rows.map((t) => (
                      <TableRow key={t.id}>
                        <TableCell>{t.description}</TableCell>
                        <TableCell className="text-slate-600">
                          {categoryLabel(t.category)}
                        </TableCell>
                        <TableCell>
                          <TemplateMeta template={t} />
                        </TableCell>
                        <TableCell className="text-right tabular">
                          {priceLabel(t.default_unit_price)}
                        </TableCell>
                        <TableCell className="text-right tabular text-slate-500">
                          {priceLabel(t.default_cost)}
                        </TableCell>
                        <TableCell className="text-right">
                          <button
                            onClick={() => remove(t)}
                            className="text-slate-400 hover:text-red-600"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </TableCell>
                      </TableRow>
                    ))}
                    {rows.length === 0 && (
                      <TableRow>
                        <TableCell
                          colSpan={6}
                          className="text-center text-slate-500 py-4"
                        >
                          No templates match
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            </div>
          );
        })}
    </Card>
  );
}

function looksLikeEmail(value) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((value || "").trim());
}

function FieldHelp({ children }) {
  return <p className="mt-1 text-xs text-slate-500">{children}</p>;
}

function UsersSection() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [form, setForm] = useState({
    email: "",
    password: "",
    name: "",
    phone: "",
    role: ROLES.ASSOCIATE,
  });
  const [open, setOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editingUserId, setEditingUserId] = useState(null);
  const [editForm, setEditForm] = useState({
    name: "",
    email: "",
    phone: "",
    role: ROLES.ASSOCIATE,
    active: true,
    password: "",
  });
  const [editPhoneWarning, setEditPhoneWarning] = useState(false);
  const load = () => api.get("/auth/users").then((r) => setUsers(r.data));
  useEffect(() => {
    load();
  }, []);
  const create = async () => {
    try {
      await api.post("/auth/users", form);
      setForm({
        email: "",
        password: "",
        name: "",
        phone: "",
        role: ROLES.ASSOCIATE,
      });
      setOpen(false);
      toast.success("User created");
      load();
    } catch (err) {
      toast.error(getApiErrorMessage(err, "Could not create user"));
    }
  };
  const startEdit = (user) => {
    const savedPhone = (user.phone || "").trim();
    const invalidSavedPhone =
      savedPhone.includes("@") || looksLikeEmail(savedPhone);
    setEditingUserId(user.id);
    setEditPhoneWarning(invalidSavedPhone);
    setEditForm({
      name: user.name || "",
      email:
        user.email ||
        (!user.email && looksLikeEmail(savedPhone) ? savedPhone : ""),
      phone: invalidSavedPhone ? "" : savedPhone,
      role: user.role || ROLES.ASSOCIATE,
      active: !!user.active,
      password: "",
    });
    if (invalidSavedPhone) {
      toast.warning?.(
        "This user had an invalid phone value and it was cleared for editing.",
      );
    }
    setEditOpen(true);
  };
  const linkGhl = () => {
    window.location.assign(`${api.defaults.baseURL}/auth/ghl/start`);
  };
  const unlinkSelfGhl = async () => {
    if (!window.confirm("Unlink your GoHighLevel account?")) return;
    await api.post("/auth/ghl/unlink");
    toast.success("GoHighLevel account unlinked");
    window.location.reload();
  };
  const adminUnlinkGhl = async (userId) => {
    if (!window.confirm("Unlink this user's GoHighLevel account?")) return;
    try {
      await api.post(`/auth/users/${userId}/ghl/unlink`);
      toast.success("GoHighLevel account unlinked");
      load();
    } catch (err) {
      toast.error(
        getApiErrorMessage(err, "Could not unlink GoHighLevel account"),
      );
    }
  };
  const saveEdit = async () => {
    if (!editingUserId) return;
    const trimmedPhone = (editForm.phone || "").trim();
    const phoneDigits = trimmedPhone.replace(/\D/g, "");
    if (trimmedPhone && phoneDigits.length !== 10) {
      toast.error("Phone must be blank or a 10-digit U.S. number.");
      return;
    }
    const payload = {
      name: (editForm.name || "").trim(),
      email: (editForm.email || "").trim(),
      phone: trimmedPhone ? phoneDigits : null,
      role: editForm.role,
      active: editForm.active,
    };
    const trimmedPassword = (editForm.password || "").trim();
    if (trimmedPassword) payload.password = trimmedPassword;
    try {
      await api.patch(`/auth/users/${editingUserId}`, payload);
      setEditOpen(false);
      setEditingUserId(null);
      toast.success("User updated");
      load();
    } catch (err) {
      toast.error(getApiErrorMessage(err, "Could not update user"));
    }
  };
  return (
    <Card className="rounded-xl border-slate-200 p-5 space-y-4">
      <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div>
            <div className="text-sm font-semibold text-slate-900">
              GoHighLevel Account Link
            </div>
            <div className="mt-1 text-xs text-slate-600">
              Status: {user?.ghl_linked ? "Linked" : "Not Linked"}
              {user?.ghl_user_email ? ` • ${user.ghl_user_email}` : ""}
              {user?.ghl_location_id
                ? ` • Location ${user.ghl_location_id}`
                : ""}
              {user?.ghl_linked_at
                ? ` • Linked ${new Date(user.ghl_linked_at).toLocaleString()}`
                : ""}
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={linkGhl}>
              Link GoHighLevel Account
            </Button>
            {user?.ghl_linked && (
              <Button variant="outline" onClick={unlinkSelfGhl}>
                Unlink GHL Account
              </Button>
            )}
          </div>
        </div>
      </div>
      <div className="flex items-center justify-between">
        <div>
          <div className="text-sm font-semibold text-slate-900">Users</div>
          <div className="text-xs text-slate-500 mt-1">
            Manager role prepares restricted access for future accounting and
            QuickBooks tools.
          </div>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button
              className="bg-[#0F2A47] hover:bg-[#14385E] text-white"
              data-testid="settings-add-user-button"
            >
              <UserPlus className="mr-2 h-4 w-4" /> Add user
            </Button>
          </DialogTrigger>
          <DialogContent autoComplete="off">
            <DialogHeader>
              <DialogTitle>Add user</DialogTitle>
            </DialogHeader>
            <div className="space-y-3" autoComplete="off">
              <div>
                <Label htmlFor="add-user-name">Name</Label>
                <Input
                  id="add-user-name"
                  name="add_user_name"
                  value={form.name}
                  autoComplete="off"
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                />
              </div>
              <div>
                <Label htmlFor="add-user-email">Email</Label>
                <Input
                  id="add-user-email"
                  name="add_user_email"
                  type="email"
                  value={form.email}
                  autoComplete="off"
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                />
              </div>
              <div>
                <Label htmlFor="add-user-phone">Phone number</Label>
                <Input
                  id="add-user-phone"
                  name="add_phone_number"
                  value={form.phone}
                  autoComplete="tel"
                  inputMode="numeric"
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                />
                <FieldHelp>Optional. Enter 10 digits only.</FieldHelp>
              </div>
              <div>
                <Label htmlFor="add-user-password">Temporary password</Label>
                <Input
                  id="add-user-password"
                  name="add_temporary_password"
                  type="password"
                  value={form.password}
                  autoComplete="new-password"
                  onChange={(e) =>
                    setForm({ ...form, password: e.target.value })
                  }
                />
              </div>
              <div>
                <Label>Role</Label>
                <Select
                  value={form.role}
                  onValueChange={(v) => setForm({ ...form, role: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={ROLES.ASSOCIATE}>Associate</SelectItem>
                    <SelectItem value={ROLES.QUOTE}>Quote User</SelectItem>
                    <SelectItem value={ROLES.PRODUCTION}>Production</SelectItem>
                    <SelectItem value={ROLES.MANAGER}>Manager</SelectItem>
                    <SelectItem value={ROLES.ADMIN}>Admin</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button
                onClick={create}
                className="w-full bg-[#0F2A47] hover:bg-[#14385E] text-white"
              >
                Create user
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
      <div className="space-y-3 md:hidden">
        {users.map((u) => (
          <div
            key={u.id}
            className="rounded-lg border border-slate-200 bg-white p-4"
          >
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="font-medium text-slate-900 break-words">
                  {u.name || "Unnamed user"}
                </div>
                <div className="mt-1 text-sm text-slate-500 break-all">
                  {u.email}
                </div>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => startEdit(u)}
                data-testid={`settings-edit-user-${u.id}`}
              >
                Edit
              </Button>
            </div>
            <div className="mt-3 grid grid-cols-2 gap-2 text-xs">
              <div className="rounded-md bg-slate-50 p-2">
                <div className="text-slate-500 uppercase">Role</div>
                <div className="font-medium text-slate-800">
                  {ROLE_LABELS[u.role] || u.role}
                </div>
              </div>
              <div className="rounded-md bg-slate-50 p-2">
                <div className="text-slate-500 uppercase">Active</div>
                <div className="font-medium text-slate-800">
                  {u.active ? "Yes" : "No"}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="hidden md:block overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>Role</TableHead>
              <TableHead>Active</TableHead>
              <TableHead>GHL</TableHead>
              <TableHead />
            </TableRow>
          </TableHeader>
          <TableBody>
            {users.map((u) => (
              <TableRow key={u.id}>
                <TableCell className="max-w-[220px] break-words">
                  {u.name}
                </TableCell>
                <TableCell className="max-w-[320px] break-all">
                  {u.email}
                </TableCell>
                <TableCell>{ROLE_LABELS[u.role] || u.role}</TableCell>
                <TableCell>{u.active ? "Yes" : "No"}</TableCell>
                <TableCell>
                  {u.ghl_linked
                    ? `Linked${u.ghl_user_email ? ` (${u.ghl_user_email})` : ""}`
                    : "Not Linked"}
                </TableCell>
                <TableCell className="text-right space-x-2">
                  {u.ghl_linked && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => adminUnlinkGhl(u.id)}
                    >
                      Unlink GHL
                    </Button>
                  )}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => startEdit(u)}
                    data-testid={`settings-edit-user-${u.id}`}
                  >
                    Edit
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent autoComplete="off">
          <DialogHeader>
            <DialogTitle>Edit user</DialogTitle>
          </DialogHeader>
          <div className="space-y-3" autoComplete="off">
            {editPhoneWarning && (
              <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
                Invalid saved phone value detected. It has been cleared from
                this edit form.
              </div>
            )}
            <div>
              <Label htmlFor="edit-user-name">Name</Label>
              <Input
                id="edit-user-name"
                name="edit_user_name"
                value={editForm.name}
                autoComplete="off"
                onChange={(e) =>
                  setEditForm({ ...editForm, name: e.target.value })
                }
              />
            </div>
            <div>
              <Label htmlFor="edit-user-email">Email</Label>
              <Input
                id="edit-user-email"
                name="edit_user_email"
                type="email"
                value={editForm.email}
                autoComplete="off"
                onChange={(e) =>
                  setEditForm({ ...editForm, email: e.target.value })
                }
              />
            </div>
            <div>
              <Label htmlFor="edit-user-phone">Phone number</Label>
              <Input
                id="edit-user-phone"
                name="edit_phone_number"
                value={editForm.phone}
                autoComplete="tel"
                inputMode="numeric"
                onChange={(e) =>
                  setEditForm({ ...editForm, phone: e.target.value })
                }
              />
              <FieldHelp>Optional. Enter 10 digits only.</FieldHelp>
            </div>
            <div>
              <Label htmlFor="edit-user-password">New password</Label>
              <Input
                id="edit-user-password"
                name="edit_new_password"
                type="password"
                value={editForm.password}
                autoComplete="new-password"
                onChange={(e) =>
                  setEditForm({ ...editForm, password: e.target.value })
                }
              />
              <FieldHelp>Leave blank to keep the current password.</FieldHelp>
            </div>
            <div>
              <Label>Role</Label>
              <Select
                value={editForm.role}
                onValueChange={(v) => setEditForm({ ...editForm, role: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={ROLES.ASSOCIATE}>Associate</SelectItem>
                  <SelectItem value={ROLES.QUOTE}>Quote User</SelectItem>
                  <SelectItem value={ROLES.PRODUCTION}>Production</SelectItem>
                  <SelectItem value={ROLES.MANAGER}>Manager</SelectItem>
                  <SelectItem value={ROLES.ADMIN}>Admin</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>GoHighLevel status</Label>
              <div className="mt-1 rounded-md border border-slate-200 p-3 text-sm text-slate-700">
                GHL status:{" "}
                {users.find((u) => u.id === editingUserId)?.ghl_linked
                  ? "Linked"
                  : "Not Linked"}
              </div>
            </div>
            <label className="flex items-center justify-between rounded-md border border-slate-200 p-3 text-sm">
              <span className="text-slate-700">Active user</span>
              <input
                type="checkbox"
                checked={editForm.active}
                onChange={(e) =>
                  setEditForm({ ...editForm, active: e.target.checked })
                }
              />
            </label>
            <Button
              onClick={saveEdit}
              className="w-full bg-[#0F2A47] hover:bg-[#14385E] text-white"
            >
              Save changes
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

function MondayLeadsImportSection() {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [confirming, setConfirming] = useState(false);

  const uploadPreview = async () => {
    if (!file) {
      toast.error("Choose a Monday CSV or Excel export first");
      return;
    }
    const formData = new FormData();
    formData.append("file", file);
    setLoading(true);
    setSummary(null);
    try {
      const result = await api.post("/imports/monday-leads/preview", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPreview(result.data);
      toast.success("Preview ready. No leads were written.");
    } catch (err) {
      toast.error(getApiErrorMessage(err, "Preview failed"));
    } finally {
      setLoading(false);
    }
  };

  const confirmImport = async () => {
    if (!preview?.rows?.length) return;
    const ok = window.confirm(
      "Create the ready/review Monday leads now? Existing duplicates will still be skipped and nothing will be overwritten.",
    );
    if (!ok) return;
    setConfirming(true);
    try {
      const result = await api.post("/imports/monday-leads/confirm", {
        rows: preview.rows,
      });
      setSummary(result.data);
      toast.success(
        `Import complete: ${result.data.created_count || 0} leads created`,
      );
    } catch (err) {
      toast.error(getApiErrorMessage(err, "Import failed"));
    } finally {
      setConfirming(false);
    }
  };

  const s = preview?.summary || {};
  const cards = [
    ["Total rows", s.total_rows ?? 0],
    ["Ready", s.ready_rows ?? 0],
    ["Duplicates", s.duplicate_rows ?? 0],
    ["Missing contact", s.missing_contact_rows ?? 0],
    ["Unmatched reps", s.missing_salesperson_match_rows ?? 0],
    ["Errors", s.invalid_rows ?? 0],
  ];

  return (
    <Card className="rounded-xl border-slate-200 p-5 space-y-5">
      <div>
        <div className="text-sm font-semibold text-slate-900">
          Monday Leads Import
        </div>
        <p className="text-xs text-slate-500 mt-1">
          Export the Monday “📞 Lead + Project List” board to CSV or Excel,
          upload it here, review the dry-run preview, then confirm only after
          checking warnings. This import does not overwrite existing leads by
          default.
        </p>
      </div>

      <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
        Preview is always dry-run first. Duplicate leads are detected by Monday
        item ID, phone, email, and normalized full name. Monday API credentials
        are not required.
      </div>

      <div className="grid gap-3 md:grid-cols-[1fr_auto] items-end">
        <div>
          <Label>Monday export file (.csv or .xlsx)</Label>
          <Input
            type="file"
            accept=".csv,.xlsx"
            onChange={(e) => setFile(e.target.files?.[0] || null)}
          />
        </div>
        <Button
          onClick={uploadPreview}
          disabled={loading || !file}
          className="bg-[#0F2A47] hover:bg-[#14385E] text-white"
        >
          {loading ? "Previewing…" : "Preview Import"}
        </Button>
      </div>

      {preview ? (
        <div className="space-y-4">
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-6">
            {cards.map(([label, value]) => (
              <div
                key={label}
                className="rounded-lg border border-slate-200 bg-white p-3"
              >
                <div className="text-xs uppercase tracking-wide text-slate-500">
                  {label}
                </div>
                <div className="mt-1 text-2xl font-semibold text-slate-900">
                  {value}
                </div>
              </div>
            ))}
          </div>

          {s.unmatched_reps?.length ? (
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm">
              <span className="font-medium text-slate-800">
                Unmatched salespeople:
              </span>{" "}
              {s.unmatched_reps.join(", ")}
            </div>
          ) : null}

          <div className="overflow-x-auto rounded-lg border border-slate-200">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Row</TableHead>
                  <TableHead>Lead name</TableHead>
                  <TableHead>Phone</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Salesperson</TableHead>
                  <TableHead>Source</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Warning/result</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(preview.rows || []).slice(0, 200).map((row) => (
                  <TableRow key={row.row_number}>
                    <TableCell>{row.row_number}</TableCell>
                    <TableCell className="min-w-[160px]">
                      {row.mapped?.lead_name || "—"}
                    </TableCell>
                    <TableCell>{row.mapped?.phone || "—"}</TableCell>
                    <TableCell>{row.mapped?.email || "—"}</TableCell>
                    <TableCell>{row.salesperson || "—"}</TableCell>
                    <TableCell>{row.mapped?.lead_source || "—"}</TableCell>
                    <TableCell>{row.mapped?.status || "—"}</TableCell>
                    <TableCell className="min-w-[220px]">
                      <span className="font-medium capitalize">
                        {row.result}
                      </span>
                      {[...(row.errors || []), ...(row.warnings || [])].length
                        ? ` — ${[...(row.errors || []), ...(row.warnings || [])].join("; ")}`
                        : ""}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          {(preview.rows || []).length > 200 ? (
            <p className="text-xs text-slate-500">
              Showing first 200 preview rows.
            </p>
          ) : null}

          <Button
            onClick={confirmImport}
            disabled={
              confirming ||
              !preview?.rows?.length ||
              s.invalid_rows === s.total_rows
            }
            className="bg-[#0F2A47] hover:bg-[#14385E] text-white"
          >
            {confirming ? "Importing…" : "Confirm Import"}
          </Button>
        </div>
      ) : null}

      {summary ? (
        <div className="rounded-lg border border-green-200 bg-green-50 p-4 text-sm text-green-900">
          <div className="font-semibold">Final import summary</div>
          <div className="mt-1">
            Created {summary.created_count || 0}, skipped duplicates{" "}
            {summary.skipped_duplicate_count || 0}, warnings{" "}
            {summary.warning_count || 0}, errors {summary.error_count || 0}.
            Imported at {summary.imported_at}.
          </div>
          {summary.unmatched_reps?.length ? (
            <div>Unmatched reps: {summary.unmatched_reps.join(", ")}</div>
          ) : null}
        </div>
      ) : null}
    </Card>
  );
}

export default function SettingsPage() {
  const { user } = useAuth();
  const [settings, setSettings] = useState(null);

  const load = () => api.get("/settings").then((r) => setSettings(r.data));
  useEffect(() => {
    load();
  }, []);

  const save = async (patch) => {
    try {
      const r = await api.patch("/settings", patch);
      setSettings(r.data);
      toast.success("Settings saved");
    } catch (err) {
      toast.error(getApiErrorMessage(err, "Save failed"));
    }
  };

  if (!isAdmin(user)) {
    return (
      <Card className="rounded-xl border-slate-200 p-12 text-center">
        <div className="text-sm text-slate-500">Settings are admin-only.</div>
      </Card>
    );
  }
  if (!settings) return <div className="text-slate-500">Loading…</div>;

  return (
    <div className="space-y-6" data-testid="settings-page">
      <div>
        <Link
          to="/dashboard"
          className="text-xs text-slate-500 hover:text-slate-700 inline-flex items-center gap-1"
          data-testid="settings-back-link"
        >
          <ArrowLeft className="h-3 w-3" /> Back to dashboard
        </Link>
        <h1 className="font-display text-3xl text-slate-900 mt-1">Settings</h1>
        <p className="text-sm text-slate-500 mt-1">
          Configure how your dealership quotes look and price.
        </p>
      </div>
      <Tabs defaultValue="company">
        <TabsList>
          <TabsTrigger value="company" data-testid="settings-tab-company">
            Company
          </TabsTrigger>
          <TabsTrigger value="catalog" data-testid="settings-tab-catalog">
            Home catalog
          </TabsTrigger>
          <TabsTrigger value="pricing" data-testid="settings-tab-pricing">
            Pricing engine
          </TabsTrigger>
          <TabsTrigger value="text" data-testid="settings-tab-text">
            Disclaimer & next steps
          </TabsTrigger>
          <TabsTrigger value="templates" data-testid="settings-tab-templates">
            Line item templates
          </TabsTrigger>
          <TabsTrigger value="users" data-testid="settings-tab-users">
            Users
          </TabsTrigger>
          <TabsTrigger value="imports" data-testid="settings-tab-imports">
            Imports
          </TabsTrigger>
        </TabsList>
        <TabsContent value="company" className="mt-4">
          <CompanySection settings={settings} save={save} />
        </TabsContent>
        <TabsContent value="catalog" className="mt-4">
          <CatalogSection />
        </TabsContent>
        <TabsContent value="pricing" className="mt-4">
          <PricingSection settings={settings} save={save} />
        </TabsContent>
        <TabsContent value="text" className="mt-4">
          <TextSection settings={settings} save={save} />
        </TabsContent>
        <TabsContent value="templates" className="mt-4">
          <TemplatesSection />
        </TabsContent>
        <TabsContent value="users" className="mt-4">
          <UsersSection />
        </TabsContent>
        <TabsContent value="imports" className="mt-4">
          <MondayLeadsImportSection />
        </TabsContent>
      </Tabs>
    </div>
  );
}
